/**
 * FE-4733 — a template's local variables: qualification by placement, and the
 * template layer of the variable merge.
 *
 * A template body is SHARED by every placement, so it cannot name one. The
 * author writes local references — `{{state}}`, a Variables-chunk
 * `key: "state"`, `data.state` in a script — and every reference is qualified to
 * the placement PATH when the instance is assembled. The path is the chain of
 * placement names from the screen down: the screen-level placement's own id,
 * then the template node id of each nested placement below it —
 * `card-text-row-gap20-20-16-i81zHgSXo.action.state`. The template node id, not
 * the assembled `<placement>~<node>` id, because every level stores its path
 * relative to itself: the card declares `action.state`, not `…~action.state`.
 *
 * The values come from the same walk, bottom-up (`effectiveLocalsOf`): at each
 * level wrap what the nested placements declare into their placement names, then
 * lay the level's own declaration over it. At the screen that yields one object
 * per placement, and it goes UNDERNEATH `screen.settings.variables`
 * (`withTemplateVariables`), so the final order, right wins, is: templates →
 * `settings.variables` → backend parameters and event writes → global `c.*`.
 *
 * MIRROR OF THE EDITOR, NOT SHARED CODE — `fractal-fe`'s
 * `features/screenTemplates/lib/templateLocalVariables.ts`, same anchors. Two
 * deliberate differences: the editor qualifies with the assembled placement id,
 * one level deep, where this qualifies with the full path; and there is no
 * regex lookbehind here, because this runs on public pages in whatever browser
 * the visitor has.
 */

type AnyRecord = Record<string, any>;

const isPlainObject = (value: any): value is AnyRecord => (
  !!value && typeof value === 'object' && !Array.isArray(value)
);

/**
 * Heads owned by other passes, never a template's to claim: `this` is row
 * scope, `c` the global store, `_c` / `_s` / `_config` the design-token and
 * config passes, `_l` localisation. A template that declares one gets neither
 * the qualification nor the merge for it — otherwise `{{_c.brand}}` inside the
 * body would be redirected into the placement and never reach its own pass.
 */
const RESERVED_HEADS = new Set(['this', 'c', '_c', '_s', '_config', '_l']);

const cloneValue = (value: any): any => (
  value && typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value
);

/** The locals a template body declares, reserved heads dropped. */
export const declaredLocalsOf = (root: any): AnyRecord => {
  const declared = isPlainObject(root) ? root.variables : undefined;
  if (!isPlainObject(declared)) return {};
  return Object.keys(declared).reduce((acc: AnyRecord, key) => {
    if (!RESERVED_HEADS.has(key)) acc[key] = declared[key];
    return acc;
  }, {});
};

const isUnresolvedPlaceholder = (value: any): boolean => (
  typeof value === 'string' && /^\{\{.*\}\}$/.test(value)
);

/**
 * Lay `upper` over `lower`, right wins, without touching either.
 *
 * Plain objects merge key by key; anything else from `upper` REPLACES — an array
 * default is one value, and index-merging `['x']` over `['a', 'b']` into
 * `['x', 'b']` is the corruption lodash's merge would produce here. An upper
 * `"{{…}}"` that never resolved does not clobber a real lower value, the same
 * guard `mergeVariableBags` keeps between the layers above this one.
 */
const layerOver = (lower: any, upper: any): any => {
  if (isPlainObject(lower) && isPlainObject(upper)) {
    const out: AnyRecord = { ...lower };
    Object.keys(upper).forEach((key) => {
      out[key] = key in out ? layerOver(out[key], upper[key]) : cloneValue(upper[key]);
    });
    return out;
  }
  if (upper === undefined) return lower;
  if (isUnresolvedPlaceholder(upper) && lower !== undefined && !isUnresolvedPlaceholder(lower)) {
    return lower;
  }
  return cloneValue(upper);
};

/** Placement names within a body: each reference below the root, not descended into. */
const nestedPlacementsOf = (root: AnyRecord): Array<{ local: string; externalId: string }> => {
  const out: Array<{ local: string; externalId: string }> = [];
  const visit = (nodes: any) => {
    (Array.isArray(nodes) ? nodes : []).forEach((node: any) => {
      if (!isPlainObject(node)) return;
      const externalId = typeof node.template === 'string' ? node.template.trim() : '';
      if (externalId && node.id !== undefined && node.id !== null) {
        out.push({ local: String(node.id), externalId });
        return;
      }
      visit(node.items);
    });
  };
  visit(root.items);
  return out;
};

/**
 * Everything a placement of `externalId` owns, relative to the placement:
 * nested placements wrapped into their names, then this template's own
 * declaration laid over them — the node overrides its children. A nested
 * placement that owns nothing adds no key.
 *
 * `ancestry` mirrors the assembly's cycle guard: a reference the assembly skips
 * as circular contributes nothing here either.
 */
export const effectiveLocalsOf = (
  externalId: string,
  templates: AnyRecord,
  rootOf: (record: any) => AnyRecord | null,
  ancestry: string[] = [],
): AnyRecord => {
  if (ancestry.indexOf(externalId) !== -1) return {};
  const root = rootOf(templates?.[externalId]);
  if (!root) return {};
  const chain = ancestry.concat(externalId);
  const nested = nestedPlacementsOf(root).reduce((acc: AnyRecord, { local, externalId: ref }) => {
    const locals = effectiveLocalsOf(ref, templates, rootOf, chain);
    if (Object.keys(locals).length && !RESERVED_HEADS.has(local)) acc[local] = locals;
    return acc;
  }, {});
  return layerOver(nested, declaredLocalsOf(root));
};

/** The template layer for layer-2 `settings.variables`: the defaults underneath. */
export const layerTemplateVariables = (templateLayer: AnyRecord, variables: any): any => {
  if (!isPlainObject(templateLayer) || !Object.keys(templateLayer).length) return variables;
  let own = variables;
  if (typeof own === 'string') {
    try { own = JSON.parse(own); } catch { own = undefined; }
  }
  return layerOver(templateLayer, isPlainObject(own) ? own : {});
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The two anchors a local reference can sit behind, as in the editor:
 *   `{{state}}` / `{{state.x}}` — first segment of a binding; a plain string
 *      `"state"` is untouched because the next character is a quote;
 *   `data.state` — the script anchor. In serialized JSON a newline or tab
 *      before it is the two characters `\n` / `\t`, hence the escape branch.
 * `{{this…}}` is never a declared local, so row scope is never rewritten.
 */
const localRefRegexFor = (name: string): RegExp => new RegExp(
  `(\\{\\{\\s*)${escapeRegExp(name)}(?=[\\s.}])`
  + `|((?:^|[^A-Za-z0-9_.$]|\\\\[nt])data\\.)${escapeRegExp(name)}(?![A-Za-z0-9_])`,
  'g',
);

const headOf = (path: string): string => path.split('.')[0];

/**
 * Qualify the Variables-chunk WRITE keys. A key is a bare dotted path with no
 * `{{` and no `data.`, so the regex cannot see it; it is walked structurally.
 */
const qualifyVariableChunkKeys = (node: any, path: string, heads: Set<string>): any => {
  if (Array.isArray(node)) return node.map((child) => qualifyVariableChunkKeys(child, path, heads));
  if (!isPlainObject(node)) return node;
  const out: AnyRecord = {};
  Object.keys(node).forEach((key) => {
    out[key] = qualifyVariableChunkKeys(node[key], path, heads);
  });
  if (Array.isArray(out.variables)) {
    out.variables = out.variables.map((row: any) => {
      if (!isPlainObject(row) || typeof row.key !== 'string') return row;
      const key = row.key.trim();
      if (!heads.has(headOf(key))) return row;
      return { ...row, key: `${path}.${key}` };
    });
  }
  return out;
};

/**
 * Qualify every reference to `heads` in an assembled subtree to `path`.
 *
 * Returns the input itself when there is nothing to qualify.
 */
export const qualifyLocalVariableRefs = <T>(subtree: T, path: string, heads: string[]): T => {
  const names = heads.filter((name) => !!name && !RESERVED_HEADS.has(name));
  if (!path || !names.length) return subtree;

  const withKeys = qualifyVariableChunkKeys(subtree, path, new Set(names));
  const json = JSON.stringify(withKeys);
  if (typeof json !== 'string') return subtree;

  const qualified = names.reduce((acc, name) => acc.replace(
    localRefRegexFor(name),
    (_whole: string, binding?: string, script?: string) => `${binding ?? script ?? ''}${path}.${name}`,
  ), json);
  return (qualified === json ? withKeys : JSON.parse(qualified)) as T;
};
